import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { observer } from 'mobx-react';
import {
  useBlocker,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import * as yup from 'yup';
import {
  Breadcrumb,
  FieldGeneratorV2,
  FormHandler,
  PageLoading,
  SnackbarApiErrorMessage,
  useDataProvider,
  useLocalization,
  useSnackbar,
  useStore,
  useUserAccess,
  UnSavedChangesDialog,
} from '@ui-framework/index';
import {
  FormFields,
  IconLibrary,
  Layout,
  PageSection,
  StackablePageButtons,
} from '@product-framework/index';
import ContentStepper from '@product-framework/components/ContentStepper';
import IconHeader from '@product-framework/components/IconHeader';
import IssueAdditionalInfo from '../../components/IssueAdditionalInfo';
import IssueAttachments from '../../components/IssueAttachments';
import IssueComments from '../../components/IssueComments';
import { CREATE_REQUEST_MESSAGE } from '../../components/IssueCreateDialog/copy';
import useIssuePermissions from '../../components/useIssuePermissions';
import {
  IssueComponentSelect,
  IssuePrioritySelect,
  IssueResolutionSelect,
  IssueStatusSelect,
  IssueTypeSelect,
} from '../../components/IssueLookupSelect';
import {
  ISSUE_API_ROOT,
  ISSUES_LIST_STORE_NAME,
} from '../../components/constants';
import {
  getErrorMessage,
  getIssueData,
  getIssueUserDisplayLabel,
  getSearchPlaceholder,
  getUnavailableIssueMessage,
  stripEmptyValues,
} from '../../components/utils';
import SxclServicesAutocomplete, {
  compactControlSx,
} from '../../components/SxclServicesAutocomplete';
import issueDetailSchema from '../../schemas/issueDetail.json';
import issueStatusSchema from '../../schemas/status.json';
import { buildIssueDetailUpdatePayload } from '../../components/issueDetailPayload';
import {
  buildAssignUserQueryParams,
  buildAssignUserValueQueryParams,
  buildIssueAssignmentPayload,
  areIssueTriageActionsDisabled,
  canReopenIssue,
  getEditableDetailFields,
  getDefaultResolutionStatusCode,
  getDetailStatusCodesForIssue,
  getAssignStateFromIssue,
  getIssueActionButtonAvailability,
  getIssueHeaderFields,
  handleIssueMutationConflict,
  getReopenStatusCode,
  getResolutionStatusCodesForIssue,
  getInlineStatusSelectionRedirect,
  isDetailFieldVisibleForRole,
  isDetailSectionVisibleForRole,
  getRoleAwareIssueDetailSections,
  normalizeIssueForDetailPage,
  shouldShowAdminEscalationNotice,
} from './utils';

const STATUS_FIELDS = ['statusCode', 'resolutionCode', 'resolutionNote'];

function HeaderCell({ label, value }) {
  return (
    <>
      <Typography variant="body1" fontWeight="bold" color="primary.main">
        {label}
      </Typography>
      <Typography
        component="span"
        variant="body1"
        fontWeight="bold"
        color="tertiary.main">
        {value || '-'}
      </Typography>
    </>
  );
}

function IssueDetailHeader({ issue }) {
  const { formatMessage } = useLocalization();

  if (!issue) {
    return null;
  }

  const headerFields = getIssueHeaderFields(issue, formatMessage);

  return (
    <Alert
      sx={{
        display: 'block',
        borderRadius: 1,
        mb: 1,
      }}
      icon={false}
      severity="info">
      <Grid2 container alignItems="center" rowSpacing={1.5}>
        <Grid2 xs={6} md={2}>
          <IconHeader
            bgColor="primary.main"
            size={40}
            Icon={IconLibrary.IssueDetail}>
            {formatMessage({ id: 'REQUEST', defaultMessage: 'Request' })}
          </IconHeader>
        </Grid2>
        <Grid2 xs={6} md={3} textAlign="center">
          <HeaderCell
            label={formatMessage({
              id: 'REQUEST_NUMBER',
              defaultMessage: 'Request Number',
            })}
            value={headerFields.issueNumber}
          />
        </Grid2>
        <Grid2 xs={6} md={2} textAlign="center">
          <HeaderCell
            label={formatMessage({
              id: 'REQUEST_STATUS',
              defaultMessage: 'Request Status',
            })}
            value={headerFields.statusLabel}
          />
        </Grid2>
        <Grid2 xs={6} md={2} textAlign="center">
          <HeaderCell
            label={formatMessage({
              id: 'PRIORITY',
              defaultMessage: 'Priority',
            })}
            value={headerFields.priorityLabel}
          />
        </Grid2>
        <Grid2 xs={6} md={2} textAlign="center">
          <HeaderCell
            label={formatMessage({
              id: 'RESOLUTION_STATUS',
              defaultMessage: 'Resolution Status',
            })}
            value={headerFields.resolutionLabel}
          />
        </Grid2>
      </Grid2>
    </Alert>
  );
}

function IssueStatusSelectForPage({ field, ...props }) {
  return <IssueStatusSelect {...props} field={field} />;
}

// Assignment state/handlers for the inline "Assigned To" control. Passed via
// context because form custom components only receive their schema field.
const IssueInlineAssignContext = createContext(null);

// Inline "Assigned To" dropdown: SA/Admin users reassign by picking a user
// directly on the field instead of a separate Reassign dialog (SXCL-2475).
// Selecting a user assigns immediately through the existing assign API, so
// the notification flow is unchanged.
function IssueAssignedToSelect({ field }) {
  const assign = useContext(IssueInlineAssignContext);
  const { formatMessage } = useLocalization();

  if (!assign) {
    return <IssueReadOnly field={field} />;
  }

  return (
    <Stack
      direction={assign.isAdminUser ? 'row' : 'column'}
      spacing={1}
      alignItems={assign.isAdminUser ? 'center' : 'stretch'}>
      {assign.isAdminUser && (
        <TextField
          select
          size="small"
          title={formatMessage({
            id: 'ASSIGNEE_ROLE',
            defaultMessage: 'Assignee Role',
          })}
          sx={{ ...compactControlSx, width: 190, flexShrink: 0 }}
          SelectProps={{
            SelectDisplayProps: {
              'aria-label': formatMessage({
                id: 'ASSIGNEE_ROLE',
                defaultMessage: 'Assignee Role',
              }),
            },
          }}
          value={assign.assignRole}
          onChange={event => assign.onRoleChange(event.target.value)}
          disabled={assign.disabled}>
          <MenuItem value="ADMIN">
            {formatMessage({
              id: 'SERVICE_XCELERATOR',
              defaultMessage: 'Service Xcelerator',
            })}
          </MenuItem>
          <MenuItem value="SERVICE_ADMINISTRATOR">
            {formatMessage({
              id: 'SERVICE_ADMINISTRATOR',
              defaultMessage: 'Service Administrator',
            })}
          </MenuItem>
        </TextField>
      )}
      <Box sx={{ flexGrow: 1, minWidth: 0, width: '100%' }}>
        <SxclServicesAutocomplete
          dataProvider={assign.dataProvider}
          endpoint={`${ISSUE_API_ROOT}/users`}
          value={assign.assigneeUserId}
          valueOption={assign.assigneeOption}
          onChange={userId => {
            // Clearing is not an unassign action; only a new selection assigns.
            if (userId && userId !== assign.assigneeUserId) {
              assign.onAssign(userId);
            }
          }}
          getOptionLabel={getIssueUserDisplayLabel}
          renderOption={(optionProps, option) => (
            <li {...optionProps} key={option.id}>
              <Box>
                <Typography variant="body2">
                  {getIssueUserDisplayLabel(option)}
                </Typography>
                {option?.email && (
                  <Typography variant="caption" color="text.secondary">
                    {option.email}
                  </Typography>
                )}
              </Box>
            </li>
          )}
          buildParams={assign.buildParams}
          buildValueParams={assign.buildValueParams}
          placeholder={getSearchPlaceholder(formatMessage)}
          noOptionsText={formatMessage({
            id: 'NO_ASSIGNABLE_USERS_FOUND',
            defaultMessage: 'No assignable users found',
          })}
          disabled={assign.disabled}
        />
      </Box>
    </Stack>
  );
}

function IssueDetailFormStateWatcher({ onDirtyChange }) {
  const {
    formState: { isDirty },
  } = useFormContext();

  useEffect(() => {
    onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  return null;
}

function IssueDetailStatusRedirectWatcher({
  currentStatusCode,
  resolutionStatusCodes,
  onRedirect,
}) {
  const { control, setValue } = useFormContext();
  const selectedStatusCode = useWatch({ control, name: 'statusCode' });
  const { dirtyFields } = useFormState({ control, name: 'statusCode' });
  const statusFieldDirty = !!dirtyFields.statusCode;

  useEffect(() => {
    const redirectStatusCode = getInlineStatusSelectionRedirect({
      selectedStatusCode,
      currentStatusCode,
      resolutionStatusCodes,
      statusFieldDirty,
    });

    if (redirectStatusCode) {
      setValue('statusCode', currentStatusCode, { shouldDirty: false });
      onRedirect(redirectStatusCode);
    }
  }, [
    selectedStatusCode,
    currentStatusCode,
    resolutionStatusCodes,
    statusFieldDirty,
    setValue,
    onRedirect,
  ]);

  return null;
}

function IssueReadOnly({ field }) {
  const { getValues } = useFormContext();
  const value = getValues(field.name);

  return (
    <Typography
      id={field.name}
      variant="subtitle2"
      color="tertiary.main"
      component="span"
      sx={{ fontWeight: 500 }}>
      {value || '-'}
    </Typography>
  );
}

function IssueFloatingButtons({ buttons }) {
  useEffect(() => {
    const previousMarginBottom = window.document.body.style.marginBottom;
    window.document.body.style.marginBottom = '120px';

    return () => {
      window.document.body.style.marginBottom = previousMarginBottom;
    };
  }, []);

  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{
        position: 'fixed',
        zIndex: 2,
        right: '50px',
        bottom: '50px',
      }}>
      {buttons.map(button => {
        const { Icon } = button;

        return (
          <Fab
            key={button.key || button.label}
            variant="extended"
            size="medium"
            color={button.color || 'primary'}
            disabled={button.disabled || false}
            onClick={button.onClick}>
            <Icon sx={{ mr: 0.6, fontSize: '18px' }} />
            {button.label}
          </Fab>
        );
      })}
    </Stack>
  );
}

function IssueDetailPage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const formRef = useRef();
  const statusFormRef = useRef();
  const sectionRefs = useRef({});
  const { formatMessage } = useLocalization();
  const snackbar = useSnackbar();
  const userAccess = useUserAccess();
  const { actions: issueActions } = useIssuePermissions();
  const store = useStore();
  const dataProvider = useDataProvider('issuesTracking');
  const [issueConfig, setIssueConfig] = useState(
    location.state?.issueConfig || null,
  );
  const [issue, setIssue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Which action is in flight, so only that button shows its busy label
  // (identical labels across buttons also collide as React keys).
  const [savingAction, setSavingAction] = useState(null);
  const [detailFormDirty, setDetailFormDirty] = useState(false);
  const [error, setError] = useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [resolveDialogStatusCode, setResolveDialogStatusCode] = useState(null);
  const [assignRole, setAssignRole] = useState('SERVICE_ADMINISTRATOR');
  const [assignUserId, setAssignUserId] = useState('');
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const detailPageBlocker = useBlocker(detailFormDirty);

  const loadIssue = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [issueResult, configResult] = await Promise.all([
      dataProvider.get(`${ISSUE_API_ROOT}/${id}`),
      dataProvider.get(`${ISSUE_API_ROOT}/config`),
    ]);
    setLoading(false);
    setIssueConfig(
      configResult.ok ? (configResult.data?.data ?? configResult.data) : null,
    );

    if (issueResult.ok) {
      setIssue(getIssueData(issueResult));
      return;
    }

    setIssue(null);
    setError(getUnavailableIssueMessage(formatMessage));
    // formatMessage is recreated every render by useLocalization; including it
    // would refetch the issue in a loop. Locale changes re-render anyway.
  }, [dataProvider, id]);

  useEffect(() => {
    loadIssue();
  }, [loadIssue]);

  useEffect(() => {
    const onBeforeUnload = event => {
      if (!detailFormDirty) {
        return;
      }
      event.preventDefault();
      const beforeUnloadEvent = event;
      beforeUnloadEvent.returnValue = '';
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [detailFormDirty]);

  const editableDetailFields = useMemo(
    () => getEditableDetailFields({ issue }),
    [issue],
  );
  const currentStatusCode = issue?.status?.code;
  const canReopenCurrentIssue = canReopenIssue({ issue, issueConfig });
  const canUpdateStatus =
    issue?.capabilities?.actions?.canUpdateStatus ?? false;
  const terminalStatusCodes = issueConfig?.terminalStatusCodes ?? [];
  const canEditDetailStatus =
    canUpdateStatus && !terminalStatusCodes.includes(currentStatusCode);
  const resolutionStatusCodes = useMemo(
    () => getResolutionStatusCodesForIssue(issue, issueConfig),
    [issue, issueConfig],
  );
  const issueActionAvailability = getIssueActionButtonAvailability({
    saving,
    detailFormDirty,
    editableDetailFields,
    canReopenIssue: canReopenCurrentIssue,
    capabilities: issue?.capabilities,
    currentStatusCode,
    issueConfig,
    canResolveStatus: resolutionStatusCodes.length > 0,
  });
  const isServiceProviderUser = userAccess.isSP?.() ?? false;
  const pageSections = useMemo(() => {
    const detailStatusCodes = getDetailStatusCodesForIssue(issue, {
      isServiceProviderUser,
      issueConfig,
    });
    const resolutionRelevantCodes = issueConfig?.resolutionStatusCodes ?? [];
    const canAssign = issue?.capabilities?.actions?.canAssign ?? false;
    return getRoleAwareIssueDetailSections({
      sections: issueDetailSchema.PAGESECTIONS.map(section => ({
        ...section,
        children: section.children
          .map(field =>
            field.name === 'statusCode'
              ? { ...field, includedCodes: detailStatusCodes }
              : field,
          )
          .filter(field =>
            isDetailFieldVisibleForRole(field, { isServiceProviderUser }),
          ),
      })),
      editableFields: editableDetailFields,
      canManageStatus: canEditDetailStatus,
      showResolutionFields: resolutionRelevantCodes.includes(currentStatusCode),
    })
      .map(section => ({
        ...section,
        // Swap AFTER the role-aware pass (which forces non-editable fields to
        // IssueReadOnly): assignable users get the inline assign dropdown.
        children: section.children.map(field =>
          field.name === 'assignedToLabel' && canAssign
            ? { ...field, control: 'IssueAssignedToSelect' }
            : field,
        ),
      }))
      .filter(section =>
        isDetailSectionVisibleForRole(section, { isServiceProviderUser }),
      );
  }, [
    canEditDetailStatus,
    currentStatusCode,
    editableDetailFields,
    isServiceProviderUser,
    issue,
    issueConfig,
  ]);

  const statusDialogSections = useMemo(() => {
    return [
      {
        locale: {
          id: 'RESOLVE',
          defaultMessage: 'Resolve',
        },
        children: issueStatusSchema.map(field =>
          field.name === 'statusCode'
            ? {
                ...field,
                includedCodes: resolutionStatusCodes,
                label: {
                  localeId: 'RESOLUTION_STATUS',
                  defaultMessage: 'Resolution Status',
                },
              }
            : field,
        ),
      },
    ];
  }, [resolutionStatusCodes]);

  const initialValues = useMemo(
    () => normalizeIssueForDetailPage(issue, formatMessage),
    // formatMessage is recreated every render; keeping it out preserves the
    // stable identity FormHandler relies on. Locale changes re-render anyway.
    [issue],
  );
  const statusDialogInitialValues = useMemo(
    () => ({
      ...initialValues,
      statusCode:
        resolveDialogStatusCode ||
        getDefaultResolutionStatusCode(issue, issueConfig),
    }),
    [initialValues, issue, issueConfig, resolveDialogStatusCode],
  );
  const statusDialogValidationSchema = useMemo(
    () =>
      yup
        .object({
          statusCode: yup.string().required(
            formatMessage({
              id: 'STATUS_IS_REQUIRED',
              defaultMessage: 'Status is required',
            }),
          ),
          resolutionCode: yup.string().required(
            formatMessage({
              id: 'RESOLUTION_IS_REQUIRED',
              defaultMessage: 'Resolution is required',
            }),
          ),
          resolutionNote: yup.string().nullable(),
        })
        .required(),
    [formatMessage],
  );

  const handleDetailDirtyChange = useCallback(isDirty => {
    setDetailFormDirty(isDirty);
  }, []);

  const requestNavigation = useCallback(
    action => {
      const isDirty = detailFormDirty || formRef.current?.isFormDirty?.();
      if (isDirty) {
        setPendingNavigation(() => action);
        return;
      }
      action();
    },
    [detailFormDirty],
  );

  const stayOnDetailPage = () => {
    setPendingNavigation(null);
    detailPageBlocker.reset?.();
  };

  const leaveDetailPage = () => {
    const action = pendingNavigation;
    setPendingNavigation(null);
    setDetailFormDirty(false);
    formRef.current?.reset(initialValues);
    setTimeout(() => {
      if (action) {
        action();
        return;
      }
      detailPageBlocker.proceed?.();
    }, 0);
  };

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => requestNavigation(() => navigate('/dashboard')),
    },
    {
      name: formatMessage({
        id: 'LIST_REQUESTS',
        defaultMessage: 'List Requests',
      }),
      icon: IconLibrary.Issues,
      onClick: () =>
        requestNavigation(() => {
          store.domain.list.reset(ISSUES_LIST_STORE_NAME);
          navigate('/issue-tracking');
        }),
    },
    {
      name: formatMessage({
        id: 'REQUEST',
        defaultMessage: 'Request',
      }),
      icon: IconLibrary.Issue,
      onClick: undefined,
    },
  ];

  const showError = errors => {
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={errors} />,
    });
  };

  const refreshIssue = async () => {
    const result = await dataProvider.get(`${ISSUE_API_ROOT}/${id}`);
    if (result.ok) {
      const nextIssue = getIssueData(result);
      setIssue(nextIssue);
      return nextIssue;
    }
    setError(getErrorMessage(result, formatMessage));
    return issue;
  };

  const updateIssueStatus = async (issueToUpdate, data) =>
    dataProvider.post(
      `${ISSUE_API_ROOT}/${issueToUpdate.id}/status`,
      stripEmptyValues({
        statusCode: data.statusCode,
        resolutionCode: data.resolutionCode,
        resolutionNote: data.resolutionNote,
        recordVersion: issueToUpdate.recordVersion,
      }),
    );

  const hasPayloadChanges = payload =>
    Object.keys(payload).some(key => key !== 'recordVersion');

  const updateIssueFromDetailForm = async (issueToUpdate, data) => {
    let nextIssue = issueToUpdate;
    let changed = false;

    const detailPayload = buildIssueDetailUpdatePayload({
      data,
      defaultValues: normalizeIssueForDetailPage(issueToUpdate),
      editableFields: editableDetailFields,
      recordVersion: issueToUpdate.recordVersion,
    });

    if (hasPayloadChanges(detailPayload)) {
      const detailResult = await dataProvider.post(
        `${ISSUE_API_ROOT}/${issueToUpdate.id}`,
        detailPayload,
      );

      if (!detailResult.ok) {
        return detailResult;
      }

      nextIssue = getIssueData(detailResult);
      changed = true;
    }

    const previousStatusCode =
      normalizeIssueForDetailPage(nextIssue).statusCode;
    const nextStatusCode = data.statusCode;
    const statusChanged =
      canUpdateStatus &&
      nextStatusCode &&
      nextStatusCode !== previousStatusCode;

    if (statusChanged) {
      const statusResult = await updateIssueStatus(nextIssue, {
        statusCode: nextStatusCode,
      });

      if (!statusResult.ok) {
        return statusResult;
      }

      nextIssue = getIssueData(statusResult);
      changed = true;
    }

    return {
      ok: true,
      data: nextIssue,
      changed,
    };
  };

  const handleSaveSuccess = nextIssue => {
    setIssue(nextIssue);
    snackbar.showMessage(
      formatMessage({
        id: 'REQUEST_WAS_SAVED_SUCCESSFULLY',
        defaultMessage: 'Request was saved successfully',
      }),
    );
  };

  const submitWithRetry = async ({ action, data }) => {
    const saveFn =
      action === 'status' ? updateIssueStatus : updateIssueFromDetailForm;
    let result = await saveFn(issue, data);

    if (result.statusCode === 409) {
      const latestIssue = await refreshIssue();
      result = await saveFn(latestIssue, data);
    }

    if (result.ok) {
      if (result.changed !== false) {
        handleSaveSuccess(getIssueData(result));
      }
      return true;
    }

    showError(result?.errors);
    return false;
  };

  const onSubmit = async (data, submitEvent) => {
    const action = submitEvent?.action || 'detail';
    const payload =
      action === 'status'
        ? Object.fromEntries(
            Object.entries(data).filter(([key]) => STATUS_FIELDS.includes(key)),
          )
        : data;

    setSaving(true);
    setSavingAction(action === 'status' ? 'resolve' : 'save');
    try {
      const saved = await submitWithRetry({ action, data: payload });
      if (saved && action === 'status') {
        setStatusDialogOpen(false);
        setResolveDialogStatusCode(null);
      }
    } finally {
      setSaving(false);
      setSavingAction(null);
    }
  };

  const onStatusDialogSubmit = data => onSubmit(data, { action: 'status' });

  const reopenIssue = async () => {
    const reopenStatusCode = getReopenStatusCode(issue, issueConfig);
    if (!reopenStatusCode) {
      return;
    }

    setSaving(true);
    setSavingAction('reopen');
    try {
      await submitWithRetry({
        action: 'status',
        data: { statusCode: reopenStatusCode },
      });
    } finally {
      setSaving(false);
      setSavingAction(null);
    }
  };

  const allowedStatusCodes = issue?.capabilities?.allowedStatusCodes ?? [];
  const canCancelRequest =
    allowedStatusCodes.includes('CANCELLED') &&
    currentStatusCode !== 'CANCELLED';
  const canMarkReadyForReview =
    currentStatusCode === 'WAITING_FOR_REPORTER' &&
    allowedStatusCodes.includes('READY_FOR_REVIEW');
  const canCreateIssue = issueActions.includes('CREATE');

  const cancelRequest = async () => {
    setSaving(true);
    setSavingAction('cancelRequest');
    try {
      const cancelled = await submitWithRetry({
        action: 'status',
        data: { statusCode: 'CANCELLED' },
      });
      if (cancelled) {
        setDetailFormDirty(false);
        store.domain.list.reset(ISSUES_LIST_STORE_NAME);
        await refreshIssue();
      }
    } finally {
      setSaving(false);
      setSavingAction(null);
    }
  };

  const markReadyForReview = async () => {
    setSaving(true);
    setSavingAction('readyForReview');
    try {
      await submitWithRetry({
        action: 'status',
        data: { statusCode: 'READY_FOR_REVIEW' },
      });
    } finally {
      setSaving(false);
      setSavingAction(null);
    }
  };

  const escalateIssue = async () => {
    setSaving(true);
    setSavingAction('escalate');
    try {
      const result = await dataProvider.post(
        `${ISSUE_API_ROOT}/${issue.id}/escalate`,
        { recordVersion: issue.recordVersion },
      );

      if (
        await handleIssueMutationConflict({ result, refreshIssue, showError })
      ) {
        return;
      }

      if (result.ok) {
        handleSaveSuccess(getIssueData(result));
        return;
      }

      showError(result?.errors);
    } finally {
      setSaving(false);
      setSavingAction(null);
    }
  };

  const canEscalateIssue = issue?.capabilities?.actions?.canEscalate ?? false;
  const canAssignIssue = issue?.capabilities?.actions?.canAssign ?? false;
  const triageActionsDisabled = areIssueTriageActionsDisabled({
    saving,
    detailFormDirty,
  });
  const isAdminUser = userAccess.isAdmin?.() ?? false;
  const showAdminEscalationNotice = shouldShowAdminEscalationNotice({
    issue,
    isServiceAdministrator: userAccess.isSA?.() ?? false,
  });

  // Keep the inline assignee-role/user selection in step with the loaded
  // issue (also used to roll back after a failed /assign call).
  useEffect(() => {
    const nextAssignState = getAssignStateFromIssue({ issue, isAdminUser });
    setAssignRole(nextAssignState.assignRole);
    setAssignUserId(nextAssignState.assignUserId);
  }, [isAdminUser, issue]);

  const assignIssueTo = async (assigneeUserId, roleOverride) => {
    if (!assigneeUserId) {
      return;
    }

    setSaving(true);
    setSavingAction('reassign');
    // Show the picked user right away; the effect above re-syncs from the
    // saved issue on success, and the error path below rolls this back.
    setAssignUserId(assigneeUserId);
    try {
      const payload = buildIssueAssignmentPayload({
        assigneeUserId,
        assigneeRole: roleOverride ?? assignRole,
        recordVersion: issue.recordVersion,
      });
      const result = await dataProvider.post(
        `${ISSUE_API_ROOT}/${issue.id}/assign`,
        payload,
      );

      if (
        await handleIssueMutationConflict({ result, refreshIssue, showError })
      ) {
        return;
      }

      if (result.ok) {
        handleSaveSuccess(getIssueData(result));
        return;
      }

      showError(result?.errors);
      const revertAssignState = getAssignStateFromIssue({ issue, isAdminUser });
      setAssignRole(revertAssignState.assignRole);
      setAssignUserId(revertAssignState.assignUserId);
    } finally {
      setSaving(false);
      setSavingAction(null);
    }
  };

  const assignCompanyId =
    assignRole === 'ADMIN'
      ? userAccess?.company?.id
      : issue?.serviceAdministrator?.id || userAccess?.company?.id;

  const buildAssignUserParams = useCallback(
    searchValue =>
      buildAssignUserQueryParams({
        companyId: assignCompanyId,
        searchValue,
      }),
    [assignCompanyId],
  );

  const buildAssignUserValueParams = useCallback(
    userId =>
      buildAssignUserValueQueryParams({ companyId: assignCompanyId, userId }),
    [assignCompanyId],
  );

  // Latest-ref indirection keeps the context value memoizable while the
  // assign handler itself closes over fresh state each render.
  const assignIssueToRef = useRef();
  assignIssueToRef.current = assignIssueTo;
  const onAssign = useCallback(userId => assignIssueToRef.current(userId), []);

  // A role change is a directory filter, not an assignment: the two role
  // pools (Service Xcelerator vs Service Administrator) are disjoint, so the
  // previously selected assignee cannot carry over. Clearing it here
  // (instead of calling /assign) means the assignment API only fires once
  // a user is actually picked, carrying whichever role is selected then.
  const handleAssignRoleChange = useCallback(nextRole => {
    setAssignRole(nextRole);
    setAssignUserId('');
  }, []);
  const assigneeOption = useMemo(
    () =>
      issue?.assignee ? { ...issue.assignee, id: issue.assignee.userId } : null,
    [issue?.assignee],
  );
  const inlineAssignContext = useMemo(
    () =>
      canAssignIssue
        ? {
            isAdminUser,
            assignRole,
            onRoleChange: handleAssignRoleChange,
            assigneeUserId: assignUserId,
            assigneeOption,
            dataProvider,
            buildParams: buildAssignUserParams,
            buildValueParams: buildAssignUserValueParams,
            disabled: triageActionsDisabled,
            onAssign,
          }
        : null,
    [
      assignRole,
      assignUserId,
      buildAssignUserParams,
      buildAssignUserValueParams,
      canAssignIssue,
      dataProvider,
      handleAssignRoleChange,
      isAdminUser,
      assigneeOption,
      onAssign,
      triageActionsDisabled,
    ],
  );

  const topPageButtons = [
    ...(canCreateIssue
      ? [
          {
            label: formatMessage(CREATE_REQUEST_MESSAGE),
            Icon: IconLibrary.IssueCreate,
            onClick: () =>
              requestNavigation(() =>
                navigate('/issue-tracking?create=true', {
                  state: { createReturnTo: location.pathname },
                }),
              ),
          },
        ]
      : []),
    {
      label: formatMessage({
        id: 'LIST_REQUESTS',
        defaultMessage: 'List Requests',
      }),
      Icon: IconLibrary.Issues,
      onClick: () => requestNavigation(() => navigate('/issue-tracking')),
    },
    {
      label: formatMessage({
        id: 'BACK',
        defaultMessage: 'Back',
      }),
      Icon: IconLibrary.Back,
      color: 'tertiary',
      onClick: () => requestNavigation(() => navigate(-1)),
    },
  ];

  const issueActionButtons = [
    {
      key: 'cancelRequest',
      label:
        saving && savingAction === 'cancelRequest'
          ? formatMessage({
              id: 'CANCELLING',
              defaultMessage: 'CANCELLING...',
            })
          : formatMessage({
              id: 'CANCEL_REQUEST',
              defaultMessage: 'CANCEL REQUEST',
            }),
      Icon: IconLibrary.Close,
      color: 'secondary',
      disabled: saving,
      hidden: !canCancelRequest,
      onClick: cancelRequest,
    },
    {
      key: 'readyForReview',
      label:
        saving && savingAction === 'readyForReview'
          ? formatMessage({
              id: 'UPDATING',
              defaultMessage: 'UPDATING...',
            })
          : formatMessage({
              id: 'READY_FOR_REVIEW',
              defaultMessage: 'READY FOR REVIEW',
            }),
      Icon: IconLibrary.IssueDetail,
      disabled: saving,
      hidden: !canMarkReadyForReview,
      onClick: markReadyForReview,
    },
    {
      key: 'save',
      label:
        saving && savingAction === 'save'
          ? formatMessage({
              id: 'SAVING_CAP_WITH_DOT',
              defaultMessage: 'SAVING...',
            })
          : formatMessage({
              id: 'UPDATE_REQUEST',
              defaultMessage: 'UPDATE REQUEST',
            }),
      Icon: IconLibrary.Save,
      disabled: issueActionAvailability.saveIssueDisabled,
      hidden: issueActionAvailability.saveIssueHidden,
      onClick: () => formRef.current?.submit({ action: 'detail' }),
    },
    {
      key: 'resolve',
      label:
        saving && savingAction === 'resolve'
          ? formatMessage({
              id: 'UPDATING',
              defaultMessage: 'UPDATING...',
            })
          : formatMessage(
              statusDialogInitialValues.statusCode ===
                issueConfig?.closeStatusCode
                ? {
                    id: 'CLOSE_REQUEST',
                    defaultMessage: 'CLOSE REQUEST',
                  }
                : {
                    id: 'RESOLVE_REQUEST_CAP',
                    defaultMessage: 'RESOLVE REQUEST',
                  },
            ),
      Icon:
        statusDialogInitialValues.statusCode === issueConfig?.closeStatusCode
          ? IconLibrary.ISSUE_STATUS_CLOSED
          : IconLibrary.IssueDetail,
      disabled: issueActionAvailability.updateStatusDisabled,
      hidden: issueActionAvailability.updateStatusHidden,
      onClick: () => {
        setResolveDialogStatusCode(null);
        setStatusDialogOpen(true);
      },
    },
    {
      key: 'reopen',
      label:
        saving && savingAction === 'reopen'
          ? formatMessage({
              id: 'REOPENING',
              defaultMessage: 'REOPENING...',
            })
          : formatMessage({
              id: 'REOPEN_REQUEST',
              defaultMessage: 'REOPEN REQUEST',
            }),
      Icon: IconLibrary.IssueReopen,
      disabled: issueActionAvailability.reopenIssueDisabled,
      hidden: issueActionAvailability.reopenIssueHidden,
      onClick: reopenIssue,
    },
    {
      key: 'escalate',
      label:
        saving && savingAction === 'escalate'
          ? formatMessage({
              id: 'ESCALATING',
              defaultMessage: 'ESCALATING...',
            })
          : formatMessage({
              id: 'ESCALATE_TO_SERVICE_XCELERATOR',
              defaultMessage: 'ESCALATE TO SERVICE XCELERATOR',
            }),
      Icon: IconLibrary.IssueDetail,
      disabled: triageActionsDisabled,
      hidden: !canEscalateIssue,
      onClick: escalateIssue,
    },
  ].filter(button => !button.hidden);

  const customComponents = {
    ...FormFields,
    IssueAssignedToSelect,
    IssueComponentSelect,
    IssuePrioritySelect,
    IssueResolutionSelect,
    IssueReadOnly,
    IssueStatusSelect: IssueStatusSelectForPage,
    IssueTypeSelect,
  };

  return (
    <Layout>
      <Grid2 container mb={1.5}>
        <Grid2 xs={12} md={6}>
          <Breadcrumb items={breadCrumb} />
        </Grid2>
        <Grid2 xs={12} md={6}>
          <StackablePageButtons buttons={topPageButtons} backButton={false} />
        </Grid2>
      </Grid2>

      {loading && (
        <PageLoading>
          {formatMessage({
            id: 'LOADING_REQUEST_DETAILS',
            defaultMessage: 'Loading request details...',
          })}
        </PageLoading>
      )}
      {error && <Alert severity="error">{error}</Alert>}

      {showAdminEscalationNotice && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          <AlertTitle>
            {formatMessage({
              id: 'ESCALATED_TO_SERVICE_XCELERATOR',
              defaultMessage: 'Escalated to Service Xcelerator',
            })}
          </AlertTitle>
          {formatMessage({
            id: 'ISSUE_ESCALATED_ADMIN_NOTICE_DESCRIPTION',
            defaultMessage:
              'This request is owned by Service Xcelerator and cannot be reassigned by your organization. A Service Xcelerator administrator can reassign it or return it to your organization.',
          })}
        </Alert>
      )}

      {!loading && issue && (
        <IssueInlineAssignContext.Provider value={inlineAssignContext}>
          <FormHandler
            ref={formRef}
            formSchema={pageSections}
            initialValues={initialValues}
            onSubmit={onSubmit}
            disableUnSavedCheck>
            <IssueDetailFormStateWatcher
              onDirtyChange={handleDetailDirtyChange}
            />
            <IssueDetailStatusRedirectWatcher
              currentStatusCode={currentStatusCode}
              resolutionStatusCodes={resolutionStatusCodes}
              onRedirect={code => {
                setResolveDialogStatusCode(code);
                setStatusDialogOpen(true);
              }}
            />
            <ContentStepper
              header={<IssueDetailHeader issue={issue} />}
              sectionRefs={sectionRefs}
              pageSections={pageSections}
              selectLastStepAtPageBottom>
              {pageSections.map(formItem => {
                const sectionLabel = formatMessage({
                  id: formItem.locale.id,
                  defaultMessage: formItem.locale.defaultMessage,
                });
                const isActivitySection =
                  formItem.locale.id === 'ADDITIONAL_INFO';
                const isAttachmentsSection =
                  formItem.locale.id === 'ATTACHMENTS';
                const isCommentsSection = formItem.locale.id === 'COMMENTS';
                let sectionContent = (
                  <FieldGeneratorV2
                    customComponents={customComponents}
                    schema={formItem.children}
                    showDirtyFields
                  />
                );

                if (isActivitySection) {
                  sectionContent = <IssueAdditionalInfo issueId={issue.id} />;
                }

                if (isAttachmentsSection) {
                  sectionContent = (
                    <IssueAttachments
                      issueId={issue.id}
                      dataProvider={dataProvider}
                      canManageAttachments={
                        issue?.capabilities?.actions?.canManageAttachments ??
                        false
                      }
                      canRemoveAttachments={
                        issue?.capabilities?.actions?.canRemoveAttachments ??
                        false
                      }
                      fileExtensions={issueConfig?.attachmentFileExtensions}
                      onChanged={refreshIssue}
                    />
                  );
                }

                if (isCommentsSection) {
                  sectionContent = (
                    <IssueComments
                      issueId={issue.id}
                      dataProvider={dataProvider}
                      userAccess={userAccess}
                      canManageComments={
                        issue?.capabilities?.actions?.canComment ?? false
                      }
                      onChanged={refreshIssue}
                    />
                  );
                }

                return (
                  <PageSection
                    key={formItem.locale.id}
                    ref={el => {
                      sectionRefs.current[formItem.locale.id] = el;
                    }}
                    id={formItem.locale.id}
                    label={sectionLabel}>
                    {sectionContent}
                  </PageSection>
                );
              })}
            </ContentStepper>
          </FormHandler>
        </IssueInlineAssignContext.Provider>
      )}
      {!loading && issue && (
        <IssueFloatingButtons buttons={issueActionButtons} />
      )}
      <UnSavedChangesDialog
        open={
          Boolean(pendingNavigation) || detailPageBlocker.state === 'blocked'
        }
        onStay={stayOnDetailPage}
        onLeave={leaveDetailPage}
      />
      {!loading && issue && (
        <Dialog
          open={statusDialogOpen}
          onClose={() => {
            setStatusDialogOpen(false);
            setResolveDialogStatusCode(null);
          }}
          maxWidth="sm"
          fullWidth>
          <DialogTitle>
            <FormattedMessage
              id="RESOLVE_REQUEST"
              defaultMessage="Resolve Request"
            />
          </DialogTitle>
          <DialogContent dividers>
            {statusDialogOpen && (
              <FormHandler
                ref={statusFormRef}
                formSchema={statusDialogSections}
                initialValues={statusDialogInitialValues}
                validationSchema={statusDialogValidationSchema}
                onSubmit={onStatusDialogSubmit}
                disableUnSavedCheck>
                <FieldGeneratorV2
                  customComponents={customComponents}
                  schema={statusDialogSections[0].children}
                />
              </FormHandler>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setStatusDialogOpen(false);
                setResolveDialogStatusCode(null);
              }}
              disabled={saving}
              color="inherit">
              {formatMessage({
                id: 'CANCEL',
                defaultMessage: 'Cancel',
              })}
            </Button>
            <Button
              variant="contained"
              disabled={saving}
              onClick={() =>
                statusFormRef.current?.submit({ action: 'status' })
              }>
              {saving
                ? formatMessage({
                    id: 'UPDATING',
                    defaultMessage: 'UPDATING...',
                  })
                : formatMessage(
                    statusDialogInitialValues.statusCode ===
                      issueConfig?.closeStatusCode
                      ? {
                          id: 'CLOSE_REQUEST',
                          defaultMessage: 'CLOSE REQUEST',
                        }
                      : {
                          id: 'RESOLVE_REQUEST_CAP',
                          defaultMessage: 'RESOLVE REQUEST',
                        },
                  )}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Layout>
  );
}

export default observer(IssueDetailPage);
